import { z } from "zod";
import { Id } from "./ids.js";

// Plain ISO dates denote UTC day boundaries; offsets retain their instant.
const HistoryDateBoundary = z
  .union([z.iso.date(), z.iso.datetime({ offset: true })])
  .refine((value) => Number.isFinite(Date.parse(value)), "Invalid ISO date boundary")
  .transform((value) => new Date(value).toISOString());

/** Historical content is evidence to inspect, never an instruction source. */
export const HistorySearchInputSchema = z.object({
  query: z.string().trim().min(1).max(500),
  before: HistoryDateBoundary.optional(),
  after: HistoryDateBoundary.optional(),
  beforeSeq: z.number().int().nonnegative().optional(),
  limit: z.number().int().min(1).max(20).optional(),
});
export const HistoryReadInputSchema = z.object({
  messageId: Id,
  linkedRunId: Id.optional(),
  runAfterId: Id.optional(),
  artifactAfterId: Id.optional(),
  outcomeAfterSeq: z.number().int().nonnegative().optional(),
  textOffset: z.number().int().nonnegative().optional(),
  direction: z.enum(["around", "older", "newer"]).optional(),
  limit: z.number().int().min(1).max(20).optional(),
  /** Return the anchor message across every stored text segment instead of one window. */
  readFullMessage: z.boolean().optional(),
});
/** One server-side fold: a whole message, or a contiguous sequence range, in a single call. */
export const HistoryScanInputSchema = z.object({
  query: z.string().trim().min(1).max(500).optional(),
  fromSeq: z.number().int().nonnegative().optional(),
  toSeq: z.number().int().nonnegative().optional(),
  fullMessageId: Id.optional(),
  maxMessages: z.number().int().min(1).max(50).optional(),
  maxTotalBytes: z.number().int().min(4_000).max(64_000).optional(),
});
export type HistorySearchInput = z.infer<typeof HistorySearchInputSchema>;
export type HistoryReadInput = z.infer<typeof HistoryReadInputSchema>;
export type HistoryScanInput = z.infer<typeof HistoryScanInputSchema>;
